'use client'

import { useState } from 'react'
import PhoneInput from 'react-phone-input-2'

import 'react-phone-input-2/lib/style.css'
import './phone-input.css'
import { cn } from '@/_lib/utils'

export type MedantePhoneInputProps = {
	value?: string | undefined
	onChange: (value: string | undefined) => void
	onBlur?: () => void
	name?: string
	id?: string
	placeholder?: string
	required?: boolean
	className?: string
	label?: string
}

/**
 * Detects country based on timezone.
 * Falls back to "sk" (Slovakia) if detection fails or if user is in Central European timezone.
 */
function detectUserCountry(): string {
	if (typeof window === 'undefined') return 'sk'

	try {
		// Get timezone - if it's Europe/Bratislava or Europe/Prague, default to Slovakia
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

		// Common Central European timezones that likely indicate Slovakia
		if (
			timezone === 'Europe/Bratislava' ||
			timezone === 'Europe/Prague' ||
			timezone === 'Europe/Vienna'
		) {
			return 'sk'
		}

		// For other cases, try to map timezone to country, but default to Slovakia
		// This is a simple approach - you could expand this based on your needs
		return 'sk'
	} catch {
		return 'sk'
	}
}

/**
 * Phone input component for Medante using react-phone-input-2.
 * Defaults to Slovakia (SK) country code.
 */
const MedantePhoneInput = ({
	value,
	onChange,
	onBlur,
	name,
	id,
	placeholder,
	required,
	className,
	label
}: MedantePhoneInputProps) => {
	const [defaultCountry] = useState<string>(() => detectUserCountry())

	const handleChange = (phone: string) => {
		// react-phone-input-2 returns the phone number with + prefix
		onChange(phone ? `+${phone}` : undefined)
	}

	return (
		<div className='phone-input-field'>
			<PhoneInput
				country={defaultCountry}
				value={value?.replace(/^\+/, '') || ''}
				onChange={handleChange}
				onBlur={onBlur}
				inputProps={{
					name,
					id,
					required,
					placeholder: placeholder || ''
				}}
				containerClass={cn('phone-input-container', className)}
				inputClass='input_text'
				buttonClass='phone-input-button'
				dropdownClass='phone-input-dropdown'
				disableSearchIcon
				countryCodeEditable={true}
			/>

			{label && <label className='input_text_placeholder'>{label}</label>}
		</div>
	)
}

export default MedantePhoneInput
